"use client";

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, PhoneCall, Clock, ShieldCheck, CircleCheck as CheckCircle2, Navigation, Zap, Phone } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const coverageAreas = [{
  name: "Jonesboro",
  zone: "Central Hub / Immediate Dispatch",
  eta: "15-30 mins",
  popular: true
}, {
  name: "Morrow",
  zone: "North Clayton County",
  eta: "20-35 mins",
  popular: false
}, {
  name: "Riverdale",
  zone: "West Clayton County",
  eta: "20-35 mins",
  popular: false
}, {
  name: "Forest Park",
  zone: "North Corridor",
  eta: "25-40 mins",
  popular: false
}, {
  name: "Lovejoy",
  zone: "South Clayton County",
  eta: "20-35 mins",
  popular: false
}, {
  name: "Stockbridge",
  zone: "East Metro / Henry County Border",
  eta: "25-40 mins",
  popular: false
}, {
  name: "Fayetteville",
  zone: "West Metro / Fayette County Border",
  eta: "25-45 mins",
  popular: false
}, {
  name: "Hampton",
  zone: "South Metro Service",
  eta: "25-45 mins",
  popular: false
}];
const dispatchHighlights = [{
  title: "24/7 Rapid Response",
  description: "Fully stocked mobile units stationed locally for fast emergency arrivals.",
  icon: Zap
}, {
  title: "No Extra Night/Weekend Fees",
  description: "Clear and upfront pricing even during midnight pipe bursts or holiday leaks.",
  icon: ShieldCheck
}, {
  title: "Direct Master Plumbers",
  description: "Speak directly with plumbing technicians who diagnose on the initial call.",
  icon: CheckCircle2
}];
export default function Coverage() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="serviceareacoverage" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-semibold uppercase tracking-wider text-xs border border-border">
            <MapPin className="size-3.5 mr-1 text-primary" />
            Clayton County & Surrounding Communities
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Fast Emergency Response Across Jonesboro & Nearby Towns
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            When a burst pipe, severe drain backup, or water heater failure threatens your property,
            our service trucks are ready to roll immediately.
          </p>
        </div>

        {/* Coverage Grid & Quick Info */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Main Coverage Cities Grid */}
          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.15
        }} className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {coverageAreas.map((area, index) => <motion.div key={area.name} variants={itemVariants} data-index={index}>
                <Card className="h-full border border-border bg-card hover:border-primary/50 shadow-xs transition-[border-color,box-shadow] duration-200">
                  <CardContent className="p-5 flex flex-col justify-between h-full">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Navigation className="size-4 text-primary shrink-0" />
                          <h3 className="font-default font-bold text-lg text-card-foreground">
                            {area.name}
                          </h3>
                        </div>
                        {area.popular && <Badge variant="default" className="text-[11px] px-2 py-0.5 font-medium bg-primary text-primary-foreground">
                            Primary Hub
                          </Badge>}
                      </div>
                      <p className="text-xs text-muted-foreground">{area.zone}</p>
                    </div>

                    <div className="mt-4 pt-3 border-t border-border/60 flex items-center justify-between text-xs font-medium">
                      <span className="text-muted-foreground flex items-center gap-1.5">
                        <Clock className="size-3.5 text-primary" />
                        Avg. Response
                      </span>
                      <span className="text-foreground font-semibold bg-muted px-2 py-0.5 rounded-md">
                        {area.eta}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>)}
          </motion.div>

          {/* Urgent Dispatch Call Card */}
          <div className="lg:col-span-5">
            <Card className="border-2 border-primary/20 bg-card shadow-lg p-6 sm:p-8 rounded-2xl relative overflow-hidden">
              {/* Emergency indicator badge */}
              <div className="flex items-center gap-2 mb-4">
                <span className="relative flex size-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
                  <span className="relative inline-flex rounded-full size-3 bg-destructive"></span>
                </span>
                <span className="text-xs font-bold uppercase tracking-wider text-destructive">
                  Active Emergency Plumber On Duty
                </span>
              </div>

              <h3 className="text-2xl sm:text-3xl font-default font-bold text-card-foreground leading-snug">
                Need Immediate Help In Your Area?
              </h3>
              
              <p className="mt-3 text-muted-foreground text-sm sm:text-base leading-relaxed">
                Skip the wait times. Call our emergency hotline now for immediate dispatch to your home or commercial facility.
              </p>

              {/* Primary Call To Action Button */}
              <div className="mt-6 p-4 rounded-xl bg-primary text-primary-foreground text-center flex flex-col items-center justify-center gap-3 shadow-md">
                <span className="text-xs font-medium uppercase tracking-wider text-primary-foreground/80">
                  24/7 Live Emergency Line
                </span>
                <a href="tel:4708479076" className="text-2xl sm:text-3xl font-bold tracking-tight text-primary-foreground hover:underline inline-flex items-center gap-2">
                  <PhoneCall className="size-6 shrink-0 animate-bounce" />
                  <span>{"(470) 847-9076"}</span>
                </a>
                <Button asChild size="lg" variant="secondary" className="w-full font-bold shadow-xs transition-colors">
                  <a href="tel:4708479076">
                    <Phone className="size-4 mr-1.5" />
                    Call For Immediate Dispatch
                  </a>
                </Button>
              </div>

              {/* Service Commitments List */}
              <div className="mt-6 space-y-3.5">
                {dispatchHighlights.map((highlight, i) => {
                const Icon = highlight.icon;
                return <div key={highlight.title} data-index={i} className="flex items-start gap-3 text-left">
                      <div className="p-1.5 rounded-md bg-secondary text-secondary-foreground shrink-0 mt-0.5">
                        <Icon className="size-4 text-primary" />
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-card-foreground">
                          {highlight.title}
                        </h4>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {highlight.description}
                        </p>
                      </div>
                    </div>;
              })}
              </div>

              {/* Secondary Navigation Link */}
              <div className="mt-6 pt-5 border-t border-border flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Looking for detailed area details?</span>
                <Link to="/service-areas/" className="font-semibold text-primary hover:underline transition-colors">
                  View All Locations
                </Link>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}